import { createSlice } from "@reduxjs/toolkit";

const calculateTotals = (items, bundles) => {
  const totalAmount =
    items.reduce((sum, item) => sum + item.totalAmount, 0) +
    bundles.reduce((sum, bundle) => sum + bundle.totalAmount, 0);
  const totalQuantity =
    items.reduce((sum, item) => sum + item.quantity, 0) +
    bundles.reduce((sum, bundle) => sum + bundle.quantity, 0);

  return { totalAmount, totalQuantity };
};

const findItemById = (items, id, type) =>
  type === "bundle"
    ? items.find((bundle) => bundle.bundle_id === id)
    : items.find((item) => item.product_id === id);

const getInitialCartState = () => {
  try {
    const savedCart = sessionStorage.getItem("cart");
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      return {
        items: parsedCart.items || [],
        bundle: parsedCart.bundle || [],
        orderSummary : parsedCart.orderSummary || {},
        totalAmount: parsedCart.totalAmount || 0,
        totalQuantity: parsedCart.totalQuantity || 0,
      };
    }
  } catch {
    sessionStorage.removeItem("cart");
  }
  return { items: [], bundle: [], orderSummary : {}, totalAmount: 0, totalQuantity: 0 };
};

const initialState = getInitialCartState();

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCart: (state, action) => {
      const {
        items = [],
        bundle = [],
        orderSummary = {},
        totalAmount = 0,
        totalQuantity = 0,
      } = action.payload || {};

      const newCartState = { items, bundle,orderSummary, totalAmount, totalQuantity };

      sessionStorage.setItem("cart", JSON.stringify(newCartState));
      return newCartState;
    },

    clearCart: () => {
      sessionStorage.removeItem("cart");
      return { items: [], bundle: [], orderSummary : {}, totalAmount: 0, totalQuantity: 0 };
    },

    addToCart: (state, action) => {
      const item = action.payload;

      if (item?.product_id) {
        const existingProduct = findItemById(state.items, item.product_id);
        
        const itemPrice =
          item?.product_details?.skus[0]?.discount_price ??
          item?.product_details?.skus[0]?.regular_price;

        if (existingProduct) {
          existingProduct.quantity += 1;
          existingProduct.totalAmount = existingProduct.quantity * itemPrice;
        } else {
          
          state.items.push({
            ...item,
            quantity: 1,
            totalAmount: itemPrice,
          });
        }
      } else if (item?.bundle_id) {
        const existingBundle = findItemById(
          state.bundle,
          item.bundle_id,
          "bundle"
        );

        if (existingBundle) {
          existingBundle.quantity += 1;
          existingBundle.totalAmount =
            existingBundle.quantity * existingBundle.unitPrice;
        } else {
          state.bundle.push({
            ...item,
            quantity: 1,
            totalAmount: item.totalAmount,
          });
        }
      }

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;

      sessionStorage.setItem("cart", JSON.stringify(state));
    },

    addByQuantity: (state, action) => {
      const newItems = action.payload;

      newItems.forEach((newItem) => {
        const existingItem = findItemById(state.items, newItem.product_id);

        if (existingItem) {
          existingItem.quantity += newItem.quantity;
          existingItem.totalAmount = existingItem.quantity * newItem.price;
        } else {
          state.items.push({
            ...newItem,
            totalAmount: newItem.price * newItem.quantity,
          });
        }
      });

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;

      sessionStorage.setItem("cart", JSON.stringify(state));
    },

    removeFromCart: (state, action) => {
      const { itemId, type } = action.payload;
      const list = type === "bundle" ? state.bundle : state.items;

      const index = list.findIndex((item) =>
        type === "bundle"
          ? item.bundle_id === itemId
          : item.product_id === itemId
      );

      if (index > -1) {
        const existingItem = list[index];
        if (existingItem.quantity > 1) {
          existingItem.quantity -= 1;
          existingItem.totalAmount -=
            type === "bundle"
              ? existingItem.unitPrice
              : existingItem.skus[0]?.regular_price;
        } else {
          list.splice(index, 1);
        }
      }

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;

      sessionStorage.setItem("cart", JSON.stringify(state));
    },

    removeItemFromCart: (state, action) => {
      const { itemId } = action.payload;

      

      state.items = state.items.filter((item) => item.product_id !== itemId);

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;

      sessionStorage.setItem("cart", JSON.stringify(state));
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  removeItemFromCart,
  addByQuantity,
  setCart,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;
